// OrderBookVirtualizedTemplate.js - TIME-BASED BATCHING (50ms window) + COLUMN RESIZING
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { VariableSizeList } from 'react-window';
import '../../common_styling.css';
import { CLEAR_TABLE_DATA, INSTRUMENT_SELECTED } from '../../common/common';
import './VirtualizedTablesTemplate.css'; 

const ROW_HEIGHT = 35;
const HEADER_HEIGHT = 52;
const SCROLLBAR_WIDTH = 15;
const BATCH_WINDOW_MS = 50; // Collect updates for 50ms, then apply all at once

const RowComponent = React.memo(({ 
  rowData,
  columns, 
  columnWidths,
  columnColors,
  handleRowClick,
  highlightOnClick,
  selectedRowId,
  style,
}) => {
  if (!rowData) return null;

  const isHighlighted = highlightOnClick && rowData.id === selectedRowId;

  return (
    <div 
      style={style}
      className={`vt-table-row-wrapper ${isHighlighted ? 'vt-highlighted-row' : ''}`}
      onClick={() => handleRowClick({ id: rowData.id, original: rowData })}
    >
      <div className="vt-table-row">
        {columns.map((col, colIndex) => {
          const cellValue = rowData[col.accessor];
          
          const cellStyle = {
            backgroundColor: columnColors[colIndex] || 
                            (colIndex === 0 ? 'var(--table-heading-color)' : 'transparent'),
            minWidth: columnWidths[colIndex],
            width: columnWidths[colIndex],
          };
          
          return (
            <div
              key={`${rowData.id}-${col.accessor}`}
              className={`vt-table-cell ${colIndex === 0 ? 'vt-sticky-column' : ''}`}
              style={cellStyle}
            >
              <span className="vt-cell-content">
                {cellValue}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

const OrderBookVirtualizedTemplate = ({
   columns,
   eventHandlers,
   tableName,
   tableId,          // ← NEW: stable id, e.g. 'buy-order-book'
   userId,           // ← NEW: current user
   onRowClick,
   onRowDoubleClick,
   filterByInstrument,
   highlightOnClick = false,
   columnColors = {},
   selectedRowId: externalSelectedRowId,
   setSelectedRowId: externalSetSelectedRowId,
   enableNullPriceSortFix = true,
   side,
   initialColumnWidths, 
   onColumnWidthsChange,
}) => {
   const [localSelectedRowId, setLocalSelectedRowId] = useState(null);
   const [localTableData, setLocalTableData] = useState([]);
   const [selectedInstrument, setSelectedInstrument] = useState(null);
   const clickTimeout = useRef(null);
   const selectedRowId = externalSelectedRowId ?? localSelectedRowId;
   const setSelectedRowId = externalSetSelectedRowId ?? setLocalSelectedRowId;
   
   const containerRef = useRef(null);
   const headerRef = useRef(null);
   const eventHandlersRef = useRef(eventHandlers);
   const listRef = useRef(null);
   const outerRef = useRef(null);
   
   const [containerHeight, setContainerHeight] = useState(600);
   const [containerWidth, setContainerWidth] = useState(800);
   const [hasVerticalScroll, setHasVerticalScroll] = useState(false);

   // TIME-BASED BATCHING
   const updateQueueRef = useRef([]);
   const flushTimerRef = useRef(null);
   const isMountedRef = useRef(true);

   const flushUpdates = useCallback(() => {
      if (updateQueueRef.current.length === 0) {
         flushTimerRef.current = null;
         return;
      }

      const updates = updateQueueRef.current.splice(0);
      flushTimerRef.current = null;

      setLocalTableData(prevData => {
         let newData = prevData;
         for (const updateFn of updates) {
            newData = updateFn(newData);
         }
         return newData;
      });
   }, []);

   const batchedSetTableData = useCallback((updateFn) => {
      updateQueueRef.current.push(updateFn);
      
      if (!flushTimerRef.current && isMountedRef.current) {
         flushTimerRef.current = setTimeout(flushUpdates, BATCH_WINDOW_MS);
      }
   }, [flushUpdates]);

   useEffect(() => {
      isMountedRef.current = true;
      return () => {
         isMountedRef.current = false;
         if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
         }
      };
   }, []);

   useEffect(() => {
      const handleEvent = (event) => {
         if (!event.detail || !event.detail.type) return;

         if (event.detail.type === INSTRUMENT_SELECTED) {
            setSelectedInstrument(event.detail.data);
            return;
         }

         if (event.detail.type === CLEAR_TABLE_DATA) {
            updateQueueRef.current = [];
            if (flushTimerRef.current) {
               clearTimeout(flushTimerRef.current);
               flushTimerRef.current = null;
            }
            setLocalTableData([]);
            setSelectedRowId(null);
            return;
         }
         
         const currentHandler = eventHandlersRef.current;
         if (currentHandler) {
            currentHandler(event.detail, batchedSetTableData);
         }
      };

      window.addEventListener(tableName, handleEvent);
      return () => {
         window.removeEventListener(tableName, handleEvent);
         if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
         }
      };
   }, [tableName, setSelectedRowId, batchedSetTableData]);

   // ========== COLUMN WIDTHS (now state, so they can be resized) ==========
   const computeDefaultWidths = useCallback(() => {
     return columns.map(col => {
       if (col.width) return col.width;
       
       const accessor = col.accessor || '';
       const header = col.Header || '';
       const headerWidth = (String(header).length * 10) + 40;
       
       let estimatedWidth = 150;
       
       if (accessor.includes('price')) {
         estimatedWidth = Math.max(110, headerWidth);
       } else if (accessor.includes('qty') || accessor.includes('quantity')) {
         estimatedWidth = Math.max(110, headerWidth);
       } else if (accessor.includes('priority')) {
         estimatedWidth = Math.max(110, headerWidth);
       } else if (accessor.includes('status') || accessor.includes('type')) {
         estimatedWidth = Math.max(110, headerWidth);
       } else {
         estimatedWidth = Math.max(120, Math.min(headerWidth, 250));
       }
       
       return estimatedWidth;
     });
   }, [columns]);

   // const savedColumnWidths = useMemo(() => {
   //    if (!userId || !tableId) return null;
   //    return null; // will be filled from Redux in the parent, see below
   // }, [userId, tableId]);

   const [columnWidths, setColumnWidths] = useState(() =>
      Array.isArray(initialColumnWidths) && initialColumnWidths.length === columns.length
         ? initialColumnWidths
         : computeDefaultWidths()
   );

   // Re-sync when columns change OR when saved widths load
   useEffect(() => {
      if (
         Array.isArray(initialColumnWidths) &&
         initialColumnWidths.length === columns.length
      ) {
         setColumnWidths(initialColumnWidths);
      } else {
         setColumnWidths(computeDefaultWidths());
      }
   }, [computeDefaultWidths, initialColumnWidths, columns.length]);

   // const startResize = useCallback((e, colIndex) => {
   //   e.preventDefault();
   //   e.stopPropagation();

   //   const startX = e.clientX;
   //   const startWidth = columnWidths[colIndex];

   //   const onMouseMove = (moveEvent) => {
   //     const delta = moveEvent.clientX - startX;
   //     setColumnWidths(prev => {
   //       const next = [...prev];
   //       next[colIndex] = Math.max(60, startWidth + delta);
   //       return next;
   //     });
   //     if (listRef.current) listRef.current.resetAfterIndex(0);
   //   };

   //   const onMouseUp = () => {
   //     document.removeEventListener('mousemove', onMouseMove);
   //     document.removeEventListener('mouseup', onMouseUp);
   //   };

   //   document.addEventListener('mousemove', onMouseMove);
   //   document.addEventListener('mouseup', onMouseUp);
   // }, [columnWidths]);

   const startResize = useCallback((e, colIndex) => {
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startWidth = columnWidths[colIndex];
      let latestWidths = columnWidths;

      const onMouseMove = (moveEvent) => {
         const delta = moveEvent.clientX - startX;
         setColumnWidths(prev => {
            const next = [...prev];
            next[colIndex] = Math.max(60, startWidth + delta);
            latestWidths = next;
            return next;
         });
         if (listRef.current) listRef.current.resetAfterIndex(0);
      };

      const onMouseUp = () => {
         document.removeEventListener('mousemove', onMouseMove);
         document.removeEventListener('mouseup', onMouseUp);
         // ← persist only once, at the end
         if (onColumnWidthsChange) onColumnWidthsChange(latestWidths);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
   }, [columnWidths, onColumnWidthsChange]);



   const totalTableWidth = useMemo(() => {
     return columnWidths.reduce((sum, width) => sum + width, 0);
   }, [columnWidths]);

   const listWidth = containerWidth > 0 ? containerWidth : totalTableWidth;

   useEffect(() => {
      eventHandlersRef.current = eventHandlers;
   }, [eventHandlers]);

   useEffect(() => {
      if (containerRef.current) {
         const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
               setContainerHeight(entry.contentRect.height);
               setContainerWidth(entry.contentRect.width);
            }
         });
         observer.observe(containerRef.current);
         return () => observer.disconnect();
      }
   }, []);

   const handleRowClick = useCallback((row) => {
      if (clickTimeout.current) {
         clearTimeout(clickTimeout.current);
         clickTimeout.current = null;
         if (onRowDoubleClick) onRowDoubleClick(row.original);
      } else {
         clickTimeout.current = setTimeout(() => {
            if (onRowClick) onRowClick(row.original);
            clickTimeout.current = null;
         }, 250);
      }
      
      if (highlightOnClick) {
         setSelectedRowId(row.id);
      }
   }, [highlightOnClick, onRowClick, onRowDoubleClick, setSelectedRowId]);

   const instrumentFilteredData = useMemo(() => {
      if (filterByInstrument === undefined) return localTableData;
      if (!filterByInstrument) return [];
      return localTableData.filter(row => row && row.instr === filterByInstrument);
   }, [localTableData, filterByInstrument]);

   const sortedData = useMemo(() => {
      if (!enableNullPriceSortFix) return instrumentFilteredData;
      
      const copy = [...instrumentFilteredData];
      
      copy.sort((a, b) => {
         const aPrice = a?.price;
         const bPrice = b?.price;
         
         const isAMrkt = aPrice === 'Mrkt';
         const isBMrkt = bPrice === 'Mrkt';
         
         if (isAMrkt && !isBMrkt) return -1;
         if (!isAMrkt && isBMrkt) return 1;
         if (isAMrkt && isBMrkt) return 0;
         
         const aNum = Number(String(aPrice).replace(/,/g, ''));
         const bNum = Number(String(bPrice).replace(/,/g, ''));
         
         if (Number.isNaN(aNum) && Number.isNaN(bNum)) return 0;
         if (Number.isNaN(aNum)) return 1;
         if (Number.isNaN(bNum)) return -1;
         
         if (side === 'Buy') {
            return bNum - aNum;
         } else if (side === 'Sell') {
            return aNum - bNum;
         }
         
         return aNum - bNum;
      });
      
      return copy;
   }, [instrumentFilteredData, enableNullPriceSortFix, side]);

   useEffect(() => {
      const availableHeight = containerHeight - HEADER_HEIGHT;
      const totalContentHeight = sortedData.length * ROW_HEIGHT;
      setHasVerticalScroll(totalContentHeight > availableHeight);
   }, [sortedData.length, containerHeight]);

   const getRowHeight = useCallback(() => ROW_HEIGHT, []);

   const Row = useCallback(({ index, style }) => {
      const rowData = sortedData[index];
      if (!rowData) return null;

      return (
         <RowComponent
            key={rowData.id || `row-${index}`}
            style={style}
            rowData={rowData}
            columns={columns}
            columnWidths={columnWidths}
            columnColors={columnColors}
            handleRowClick={handleRowClick}
            highlightOnClick={highlightOnClick}
            selectedRowId={selectedRowId}
         />
      );
   }, [sortedData, columns, columnWidths, columnColors, handleRowClick, highlightOnClick, selectedRowId]);

   useEffect(() => {
      const outerElement = outerRef.current;
      const headerElement = headerRef.current;
      
      if (!outerElement || !headerElement) return;

      const handleScroll = (e) => {
         headerElement.scrollLeft = e.target.scrollLeft;
      };

      outerElement.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
         outerElement.removeEventListener('scroll', handleScroll);
      };
   }, [containerWidth, containerHeight, sortedData.length]);

   const outerElementType = useMemo(
      () => React.forwardRef((props, ref) => {
         return (
            <div
               ref={(node) => {
                  outerRef.current = node;
                  if (typeof ref === 'function') {
                     ref(node);
                  } else if (ref) {
                     ref.current = node;
                  }
               }}
               {...props}
            />
         );
      }),
      []
   );

   return (
      <div className="vt-virtualized-table-container" ref={containerRef}>
         <div 
           ref={headerRef} 
           className="vt-table-header-wrapper"
           style={{ 
             paddingRight: hasVerticalScroll ? `${SCROLLBAR_WIDTH}px` : '0px'
           }}
         >
           <div className="vt-table-header" style={{ minWidth: totalTableWidth }}>
             <div className="vt-header-row">
               {columns.map((column, colIdx) => (
                 <div
                   key={column.accessor || `col-${colIdx}`}
                   className={`vt-table-header-cell ${colIdx === 0 ? 'vt-sticky-column' : ''}`}
                   style={{
                     minWidth: columnWidths[colIdx],
                     width: columnWidths[colIdx],
                     position: 'relative',
                   }}
                 >
                   <span className="vt-header-content">
                     {column.Header}
                   </span>

                   <div
                     className="vt-resize-handle"
                     onMouseDown={(e) => startResize(e, colIdx)}
                   />
                 </div>
               ))}
             </div>
           </div>
         </div>

         <div className="vt-table-body">
           {sortedData.length > 0 ? (
             <VariableSizeList
               ref={listRef}
               height={containerHeight - HEADER_HEIGHT}
               itemCount={sortedData.length}
               itemSize={getRowHeight}
               width={listWidth}
               outerElementType={outerElementType}
               overscanCount={10}
             >
               {Row}
             </VariableSizeList>
           ) : (
             <div className="vt-no-data-message">
               {selectedInstrument 
                 ? `No ${side} orders for ${selectedInstrument}` 
                 : 'Select an instrument to view orders'}
             </div>
           )}
         </div>
      </div>
   );
};

export default OrderBookVirtualizedTemplate;